import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type { Nullable } from 'corgi-types';
import type {
  BorderProps,
  BorderRadiusProps,
  FontSizeProps,
  LineHeightProps,
  SpaceProps,
} from 'styled-system';
import { border, borderRadius, fontSize, lineHeight, space, themeGet } from 'styled-system';

import { Flex } from 'common/components/Flex';
import ImageOption from 'common/components/ImageOption';
import ArticleSmallPrice, { RenderingStrategy } from 'common/components/Price/ArticleSmallPrice';
import { Radio } from 'common/components/Radio';
import { Text } from 'common/components/Text';
import { useKeyboardSelect } from 'common/hooks/keyboard';
import { useLocalization } from 'common/providers/LocaleProvider';
import type { CursorProps } from 'common/theme/system-utilities';
import { cursor } from 'common/theme/system-utilities';
import { RenderingType } from '../enums';
import type { ArticleVariationGroupValues } from './types';

const RADIO_ICON_SIZE = 24;

interface ConfigurationItemProp {
  groupValue: Pick<ArticleVariationGroupValues, 'title' | 'imageUrl' | 'article' | 'url'>;
  renderingType: RenderingType;
  selectedValue: Nullable<string>;
  onClick: (key: string) => void;
}

const UnstyledContainerListItem = styled(Flex)<
  BorderRadiusProps & BorderProps & CursorProps & SpaceProps
>`
  ${borderRadius};
  ${border};
  ${cursor};
  ${space};
  &:hover {
    border-color: ${themeGet('colors.sys.neutral.border.default')};
  }

  &:focus-visible {
    outline-color: ${themeGet('colors.sys.secondary.border.default')};
  }

  &:first-of-type {
    margin-top: ${themeGet('space.sp_0')};
  }
`;

const ContainerListItem = (props: ComponentProps<typeof UnstyledContainerListItem>) => (
  <UnstyledContainerListItem
    as="li"
    border="1px solid transparent"
    borderRadius="medium"
    cursor="pointer"
    p="sp_12"
    mt="sp_4"
    {...props}
  />
);

const UnstyledLabel = styled(Text)``;

const Label = (props: ComponentProps<typeof UnstyledLabel>) => (
  <UnstyledLabel
    color="sys.neutral.text.strong"
    fontSize="fs_16"
    fontWeight="bold"
    lineHeight="lh_24"
    {...props}
  />
);

const UnstyledStyledArticleSmallPrice = styled(ArticleSmallPrice)<FontSizeProps & LineHeightProps>`
  ${fontSize};
  ${lineHeight};
`;

const StyledArticleSmallPrice = (props: ComponentProps<typeof UnstyledStyledArticleSmallPrice>) => (
  <UnstyledStyledArticleSmallPrice fontSize="fs_16" lineHeight="lh_24" {...props} />
);

const UnstyledFastDelivery = styled(Text)``;

const FastDelivery = (props: ComponentProps<typeof UnstyledFastDelivery>) => (
  <UnstyledFastDelivery
    color="sys.success.text.default"
    fontSize="fs_14"
    lineHeight="lh_20"
    {...props}
  />
);

const UnstyledItemBody = styled(Flex)``;

const ItemBody = (props: ComponentProps<typeof UnstyledItemBody>) => (
  <UnstyledItemBody flex={1} flexDirection="column" mr="sp_12" {...props} />
);

const ConfigurationItem = ({
  groupValue: { title, imageUrl, article, url },
  renderingType,
  selectedValue,
  onClick,
}: ConfigurationItemProp) => {
  const { t } = useLocalization();
  const onKeyDown = useKeyboardSelect(() => url && onClick(url));
  const { regular, special, rrp, measurement } = article?.prices;

  return (
    <ContainerListItem onClick={() => url && onClick(url)} tabIndex={0} onKeyDown={onKeyDown}>
      {renderingType === RenderingType.Icon && (
        <Flex alignItems="center" mr="sp_12">
          <ImageOption alt={title} url={imageUrl} />
        </Flex>
      )}
      <ItemBody>
        <Label>{title}</Label>
        {regular && (
          <StyledArticleSmallPrice
            regularPrice={regular}
            specialPrice={special}
            rrpPrice={rrp}
            measurement={measurement}
            energyClass={article.energyClass}
            renderingStrategy={RenderingStrategy.Horizontal}
          />
        )}
        {article?.delivery_v2?.flags?.fastDelivery && (
          <FastDelivery>{t('h24_immediately_available')}</FastDelivery>
        )}
      </ItemBody>
      <Flex alignItems="center">
        <Radio
          aria-label={title}
          defaultChecked={url === selectedValue}
          iconSize={RADIO_ICON_SIZE}
          label=""
          name="configuration-trap"
          value={url ?? ''}
        />
      </Flex>
    </ContainerListItem>
  );
};

export default ConfigurationItem;
